import axios from 'axios';

// 统一走同源相对路径：生产跟随页面 http/https 避免 Mixed Content，
// 开发环境由 vue.config.js 的 devServer 代理转发到后端
const API_BASE_URL = process.env.VUE_APP_API_BASE_URL || '/api';

console.log('API基础URL:', API_BASE_URL);

// 全局配置
axios.defaults.withCredentials = true; // 允许跨域请求携带凭证

// 为axios创建一个实例，统一配置
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  timeout: 60000 // 增加请求超时时间到60秒
});

// 设置请求和响应拦截器
axiosInstance.interceptors.request.use(
  config => {
    // 记录请求详情
    console.log(`发送请求到: ${config.url}`);
    console.log('请求方法:', config.method);
    console.log('请求头:', config.headers);
    console.log('请求数据:', config.data);
    return config;
  },
  error => {
    console.error('请求拦截器错误:', error);
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  response => {
    console.log('响应状态:', response.status);
    console.log('响应数据:', response.data);
    return response;
  },
  error => {
    if (error.response) {
      console.error('响应错误状态:', error.response.status);
      console.error('响应错误数据:', error.response.data);
      console.error('响应错误头:', error.response.headers);
    } else if (error.request) {
      console.error('无响应:', error.request);
      console.error('请求配置:', error.config);
    } else {
      console.error('请求设置错误:', error.message);
    }
    return Promise.reject(error);
  }
);

const authApi = {

  // 获取软件更新信息
  getUpdateInfo: () => axiosInstance.get(`/get_update_info`),
  
  // 获取验证码
  register: async (userData) => 
    axiosInstance.post(`/register`, userData),

  getAllUsers: (params) => axiosInstance.get(`/get_all_users`, { params }),
  
   // 管理员确认请求
  confirmRequest: (userId, licenseDuration, authVersion) => 
    axiosInstance.post(`/confirm_request`, {
      user_id: userId,
      license_duration: licenseDuration,
      auth_version: authVersion
    }),

  // 获取用户详情（如果有这个接口的话）
  getUserDetails: (userId) => axiosInstance.get(`/get_user_details/${userId}`),
  
  // 获取验证码
  getCaptcha: (sessionId) => {
    const url = `/captcha?session_id=${sessionId}`;
    return axiosInstance.get(url, { responseType: 'blob' })
      .then(response => {
        return URL.createObjectURL(new Blob([response.data]));
      });
  },
  
  // 管理员登录 - 直接使用完整URL，绕过baseURL
  adminLogin: (username, password) => {
    console.log('登录请求:', { username, password: '******' });
    return axiosInstance.post('/admin/login', { username, password });
  },
  
  // 刷新令牌
  refreshToken: (refreshToken) => 
    axiosInstance.post(`/admin/refresh`, { refresh_token: refreshToken }),
  
  // 修改管理员密码
  changePassword: (currentPassword, newPassword) => 
    axiosInstance.post(`/admin/change-password`, 
      { current_password: currentPassword, new_password: newPassword },
      { 
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('access_token')}` 
        }
      }
    ),
  
  // 创建新管理员（仅超级管理员可用）
  createAdmin: (adminData) => 
    axiosInstance.post(`/admin/create`, adminData, {
      headers: { 
        'Authorization': `Bearer ${localStorage.getItem('access_token')}` 
      }
    }),
  
  // 获取管理员列表（仅超级管理员可用）
  listAdmins: () => 
    axiosInstance.get(`/admin/list`, {
      headers: { 
        'Authorization': `Bearer ${localStorage.getItem('access_token')}` 
      }
    }),
  
  // 添加请求拦截器，自动添加认证头和处理令牌过期
  setupInterceptors: (router) => {
    // 请求拦截器
    axiosInstance.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('access_token');
        if (token) {
          config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
    
    // 响应拦截器
    axiosInstance.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error) => {
        const originalRequest = error.config;
        
        // 如果是认证失败（401）且不是刷新令牌请求，尝试刷新令牌
        if (error.response && error.response.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          
          try {
            const refreshToken = localStorage.getItem('refresh_token');
            if (!refreshToken) {
              // 没有刷新令牌，清除所有认证信息并导航到登录页面
              localStorage.removeItem('access_token');
              localStorage.removeItem('refresh_token');
              localStorage.removeItem('user_info');
              localStorage.removeItem('isLogin');
              router.push('/login');
              return Promise.reject(error);
            }
            
            // 尝试刷新访问令牌
            const response = await authApi.refreshToken(refreshToken);
            
            // 更新访问令牌
            const newAccessToken = response.data.access_token;
            localStorage.setItem('access_token', newAccessToken);
            
            // 更新原始请求的认证头并重新发送
            originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
            return axiosInstance(originalRequest);
          } catch (refreshError) {
            // 刷新令牌也失败，清除所有认证信息并导航到登录页面
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('user_info');
            localStorage.removeItem('isLogin');
            router.push('/login');
            return Promise.reject(refreshError);
          }
        }
        
        return Promise.reject(error);
      }
    );
  },
  
  // 退出登录
  logout: () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_info');
    localStorage.removeItem('isLogin');
  },

  // 更新用户状态（如果有这个功能的话）
  updateUserStatus: (userId, status) => 
    axiosInstance.put(`/update_user_status`, { user_id: userId, status }),

  // 新增授权
  addNewAuth: (authData) => axiosInstance.post(`/add_new_auth`, {
    ...authData,
    licenseDuration: parseInt(authData.licenseDuration, 10) // 确保 licenseDuration 是整数
  }),

  downloadSoftware: async (filename, retries = 3) => {
    if (typeof filename !== 'string' || !filename) {
      throw new Error('Invalid filename. Expected a non-empty string.');
    }

    console.log(`Attempting to download file: ${filename}`); // 添加日志

    for (let attempt = 0; attempt < retries; attempt++) {
      try {
        let downloadUrl = `${API_BASE_URL}/download/${encodeURIComponent(filename)}`;
        console.log(`Requesting download from: ${downloadUrl}`); // 添加日志

        const response = await axiosInstance.get(`/download/${encodeURIComponent(filename)}`, {
          responseType: 'blob',
          timeout: 30000, // 30秒超时
        });
        
        const contentDisposition = response.headers['content-disposition'];
        let downloadFilename = filename;
        if (contentDisposition) {
          const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
          const matches = filenameRegex.exec(contentDisposition);
          if (matches != null && matches[1]) {
            downloadFilename = matches[1].replace(/['"]/g, '');
          }
        }

        const blob = new Blob([response.data], { type: response.headers['content-type'] });
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', downloadFilename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);

        return true; // 下载成功
      } catch (error) {
        console.error(`下载失败 (尝试 ${attempt + 1}/${retries}):`, error);
        if (attempt === retries - 1) {
          // 如果是最后一次尝试，则抛出错误
          throw new Error(`下载失败：${error.message}`);
        }
        // 等待一段时间后重试
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
    }
  },

  getAllLoginLogs: (params) => 
    axiosInstance.get(`/get_all_login_logs`, { params }),

  getAllBugReports: (params) => 
    axiosInstance.get(`/get_all_bug_reports`, { params }),

  submitInterest: (userData) => 
    axiosInstance.post(`/submit_interest`, userData),

  getAllInterestedUsers: (params) => 
    axiosInstance.get(`/get_all_interested_users`, { params }),

  markInterestedUserAsProcessed: (userId) => 
    axiosInstance.post(`/mark_interested_user_as_processed`, { user_id: userId }),

};

export default authApi;